/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useCallback, useContext, useRef, useState } from 'react'

export type ToastType = 'success' | 'error' | 'warning' | 'info'

export interface ToastData {
  id: number
  message: string
  type: ToastType
  visible: boolean
}

interface ToastContextValue {
  toast: ToastData | null
  showToast: (opts: { message: string; type: ToastType }) => void
  hideToast: () => void
  clearToast: () => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

let nextId = 0

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toast, setToast] = useState<ToastData | null>(null)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearToast = useCallback(() => setToast(null), [])

  const hideToast = useCallback(() => {
    setToast((prev) => (prev ? { ...prev, visible: false } : null))
    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = setTimeout(clearToast, 300)
  }, [clearToast])

  const showToast = useCallback(
    ({ message, type }: { message: string; type: ToastType }) => {
      if (timerRef.current) clearTimeout(timerRef.current)
      const id = ++nextId
      setToast({ id, message, type, visible: true })
      timerRef.current = setTimeout(hideToast, 4000)
    },
    [hideToast],
  )

  return (
    <ToastContext.Provider value={{ toast, showToast, hideToast, clearToast }}>
      {children}
    </ToastContext.Provider>
  )
}

export const useToast = (): ToastContextValue => {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error('useToast must be used inside ToastProvider')
  return ctx
}
